import type {
  FreeField,
  MultipleChoiceFilter,
  SearchFilter,
  SingleChoiceFilter,
} from 'generated/resources/datatable.graphql'

import { STRING_FILTER_KINDS } from 'modules/core/components/DataTable/constants'
import type { Filter as FilterType } from 'modules/core/components/DataTable/types'
import Filter from 'modules/core/components/Filter'

import Header from './Header'

type Props<T> = {
  filter: FilterType<T>
  onValueChange: (value: unknown) => void
  onCategoryChange: (category: FilterType<T>['category']) => void
  defaultOpen?: boolean
}

export default function StringFilter<T>({
  filter: dataTableFilter,
  onValueChange,
  onCategoryChange,
  defaultOpen,
}: Props<T>) {
  const { filter, category, value } = dataTableFilter

  const showHeader = filter.categories.length > 1
  const header = showHeader && (
    <Header filter={filter} category={category} onCategoryChange={onCategoryChange} />
  )

  const commonProps = {
    label: filter.title,
    onValueChange,
    header,
  }

  switch (category.kind) {
    case STRING_FILTER_KINDS.SINGLE_CHOICE:
      return (
        <Filter.SingleChoice
          options={category.options}
          value={value as SingleChoiceFilter<string>['value']}
          defaultOpen={defaultOpen}
          {...commonProps}
        />
      )
    case STRING_FILTER_KINDS.MULTIPLE_CHOICE:
      return (
        <Filter.MultipleChoice
          options={category.options as MultipleChoiceFilter<string>['options']}
          value={value as MultipleChoiceFilter<string>['value']}
          defaultOpen={defaultOpen}
          {...commonProps}
          hideSearch={category.hideSearch}
        />
      )
    case STRING_FILTER_KINDS.SEARCH:
      return (
        <Filter.Search
          value={value as SearchFilter['value']}
          defaultOpen={defaultOpen}
          {...commonProps}
        />
      )
    case STRING_FILTER_KINDS.FREE_FIELD:
      return (
        <Filter.FreeField
          value={value as FreeField<string>['value']}
          description={category.description}
          errorMessage={category.errorMessage}
          options={category.options}
          validation={category.validation}
          defaultOpen={defaultOpen}
          {...commonProps}
          fieldType="string"
        />
      )
  }

  throw new Error(`Unknown filter kind: ${category.kind}`)
}
